import { reportingKeys } from './use-reporting-query-keys';

import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * The reporting widget data sources whose rows frontend mutations can change.
 * MUST mirror `RPC_FUNCTION_MAP` in
 * `backend/supabase/functions/reporting-widget-data/lib/request-handler.ts` (the
 * deprecated `emails` alias shares the correspondences RPC and needs no
 * separate entry). Adding a data source there means adding it here AND wiring
 * its mutation settle path through {@link invalidateReportingAggregates}.
 * The registry test pins this list against the wired settle paths within the
 * frontend; the cross-runtime mirror to the Deno map is by convention — this
 * constant is the grep target when either side changes.
 */
export const REPORTING_AGGREGATE_SOURCES = [
  'tasks',
  'correspondences',
  'constituents',
  'email_drafts',
  'assignments',
] as const;

const swallow = () => null;

/** Trending's marks are applied this long after the last one gathered... */
const TREND_MARK_QUIET_MS = 2000;
/** ...or this long after the first, while marks keep coming. */
const TREND_MARK_MAX_WAIT_MS = 10_000;

/** Marks gathered for one query client; `null` stands for every office. */
interface GatheredTrendMarks {
  readonly offices: Set<string | null>;
  quietTimer: ReturnType<typeof setTimeout> | undefined;
  maxWaitTimer: ReturnType<typeof setTimeout> | undefined;
}

const gatheredTrendMarks = new WeakMap<QueryClient, GatheredTrendMarks>();

function applyGatheredTrendMarks(queryClient: QueryClient): void {
  const gathered = gatheredTrendMarks.get(queryClient);
  if (!gathered) {
    return;
  }
  clearTimeout(gathered.quietTimer);
  clearTimeout(gathered.maxWaitTimer);
  gathered.quietTimer = undefined;
  gathered.maxWaitTimer = undefined;
  const offices = [...gathered.offices];
  // Marks that arrive while this application awaits its cancellation start the next one.
  gathered.offices.clear();
  const queryKeys: QueryKey[] = offices.includes(null)
    ? [reportingKeys.homeTrends()]
    : offices.map((office) => [...reportingKeys.homeTrends(), office]);
  // A run under way may have read before the change, so it is cancelled first; the
  // cancellation reverts it, and the mark that follows is what nothing can then clear.
  Promise.all(queryKeys.map(async (queryKey) => await queryClient.cancelQueries({ queryKey })))
    .then(
      async () =>
        await Promise.all(
          queryKeys.map(
            async (queryKey) =>
              await queryClient.invalidateQueries({ queryKey, refetchType: 'active' }),
          ),
        ),
    )
    .catch(swallow);
}

/**
 * Mark the agentic home's Trending panel stale for an office (every office when none is given),
 * gathered: applied two seconds after the last mark, or ten after the first while they keep
 * coming, so a change whose settle reaches here more than once usually marks once. See
 * docs/plans/2026-10-14-feat-agentic-home-trending-plan.md ("Invalidation") for what that
 * coalescing in time does and does not promise.
 */
export function markHomeTrends(queryClient: QueryClient, orgId?: string): void {
  let gathered = gatheredTrendMarks.get(queryClient);
  if (!gathered) {
    gathered = { offices: new Set(), quietTimer: undefined, maxWaitTimer: undefined };
    gatheredTrendMarks.set(queryClient, gathered);
  }
  gathered.offices.add(orgId ?? null);
  clearTimeout(gathered.quietTimer);
  gathered.quietTimer = setTimeout(() => {
    applyGatheredTrendMarks(queryClient);
  }, TREND_MARK_QUIET_MS);
  gathered.maxWaitTimer ??= setTimeout(() => {
    applyGatheredTrendMarks(queryClient);
  }, TREND_MARK_MAX_WAIT_MS);
}

/**
 * Mark reporting widget-data aggregates stale after a mutation changes rows
 * they count. Org-scoped when the org is known (the widget key carries the
 * org id, so other orgs' cached tiles stay untouched); the unscoped prefix
 * otherwise, correct but coarser. Marks the agentic home's Trending panel too
 * ({@link markHomeTrends}).
 *
 * Default `refetchType: 'active'` on purpose: a visible dashboard refetches
 * now, an Activity-hidden Reporting tab is only marked and is read again on
 * switch-back (a tile holding an answer twice: its own refetch on mount, then
 * the catch-up in `tab-content-renderer` cutting that read short and starting
 * another). See docs/solutions/performance-issues/activity-hidden-tabs.md and
 * .claude/rules/frontend.md → Server-Side Aggregates.
 */
export function invalidateReportingAggregates(queryClient: QueryClient, orgId?: string): void {
  const queryKey = orgId ? [...reportingKeys.widgetData(), orgId] : reportingKeys.widgetData();
  queryClient.invalidateQueries({ queryKey, refetchType: 'active' }).catch(swallow);
  markHomeTrends(queryClient, orgId);
}
